//@ts-check
import { Flex, Heading, Image, View } from '@adobe/react-spectrum';
import React from 'react';

import accreditedSolutionBadge from '../../../../../../static/partner-badges/accredited-solution.svg';
import rapidDeploymentSolutionBadge from '../../../../../../static/partner-badges/rapid-deployment-solution.svg';

import useLabeledProducts from '../../../../../actions/curatorApi/selectors/exchangeUi/useLabeledProducts';
import useLabeledIndustries from '../../../../../actions/curatorApi/selectors/industries/useLabeledIndustries';
import CompactEnum from '../../../../components/CompactEnum';
import RegionList from '../../../../components/RegionList';
import WithSkeleton from '../../../../components/WithSkeleton';
import { useStore } from '../../../store';

/** @type {Array<{ key: string, src: string, alt: string }>} */
const badges = [
  { key: 'ACCREDITED', src: accreditedSolutionBadge, alt: 'Accredited Partner Solution' },
  {
    key: 'RAPID_DEPLOYMENT',
    src: rapidDeploymentSolutionBadge,
    alt: 'Rapid Deployment Partner Solution',
  },
];

const SlnSidebarMetaInfo = () => {
  /** @type {import('@action-types/ecApp').AppDetails} */
  const detailScreen = useStore((state) => state.detailScreen || {});
  const { solutionDetails, categories, hostApps } = detailScreen;
  const { labeledIndustries, industriesQuery } = useLabeledIndustries(categories);
  const { labeledProducts, exchangeUiQuery } = useLabeledProducts(hostApps);
  const detailScreenLoading = useStore((state) => state.detailScreenLoading);
  const isLoading = detailScreenLoading || industriesQuery.isFetching || exchangeUiQuery.isLoading;
  const badge = badges.find((badge) => detailScreen?.badges?.includes(badge.key));
  const geographies = solutionDetails?.geographies;
  const thirdPartyIntegrations = solutionDetails?.internalFields?.thirdPartyIntegrations;

  const metaInfoSections = [
    {
      title: 'Adobe applications used in this solution',
      content: labeledProducts?.length > 0 && <CompactEnum maxShown={5} values={labeledProducts} />,
    },
    {
      title: 'Industries',
      content: labeledIndustries?.length > 0 && (
        <CompactEnum maxShown={5} values={labeledIndustries.map(({ label }) => label)} />
      ),
    },
    {
      title: 'Regions solutions sales into',
      content: geographies?.length > 0 && <RegionList regions={geographies} />,
    },
    {
      title: 'Typical sales cycle',
      content: solutionDetails?.internalFields?.typicalSalesCycle,
    },
    {
      title: 'Typical implementation time',
      content: solutionDetails?.internalFields?.typicalImplementationTime,
    },
    {
      title: 'Number of times deployed',
      content: solutionDetails?.internalFields?.numberOfDeploymentsForTheSolution,
    },
    {
      title: 'Time to value',
      content: solutionDetails?.internalFields?.timeToValue,
    },
    {
      title: '3rd party integrations',
      content: thirdPartyIntegrations?.length > 0 && <CompactEnum maxShown={5} values={thirdPartyIntegrations} />,
    },
  ].filter(({ content }) => content);

  return (
    <View
      backgroundColor="static-white"
      borderRadius="regular"
      padding="size-200"
      borderWidth="thin"
      borderColor="gray-200"
      marginBottom="size-400"
    >
      <WithSkeleton count={9}>
        {!isLoading && (
          <Flex gap="size-200" direction="column">
            {badge && (
              <Image
                src={`/${badge.src.replace(/^\//, '')}`}
                alt={badge.alt}
                marginBottom="size-100"
                maxWidth="178px"
              />
            )}
            {metaInfoSections.map(({ title, content }) => (
              <div key={title}>
                <Heading level={4} marginTop="0" marginBottom="size-50">
                  {title}
                </Heading>
                {content}
              </div>
            ))}
          </Flex>
        )}
      </WithSkeleton>
    </View>
  );
};

export default SlnSidebarMetaInfo;
